/* eslint-disable dot-notation */
/* eslint-disable quote-props */
import '../theme/css/global.css';
import {
  AppBar, Box, Button, IconButton, Link, ListItemIcon, MenuItem, MenuList, Popover, styled, Toolbar, Tooltip, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import {
  useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { ArrowBack } from '@mui/icons-material';
import Utils from '../utils/Utils';
import User from '../models/User';
import { useAppDispatch, useAppSelector } from '../store/Hooks';
import translator from '../theme/translator.json';

import ErrorService from '../services/ErrorService';
import Spinner from './Spinner';
import UserService from '../services/UserService';
import PromptCreateMunicipality from './PromptCreateMunicipality';
import Permissions from '../models/enums/Permissions';
import PromptCreateForm from './promptCreateForm';
import PromptExport from './PromptExport';

type Props = {
  onSidebarOpen: any,
  title: string,
  showBack?: boolean
};

const NavbarRoot = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
}));

const Navbar: React.FC<Props> = ({ onSidebarOpen, title, showBack }) => {
  const user = useAppSelector((state) => state.user.user) as User;

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const settingsRef = useRef(null);
  const createRef = useRef(null);

  const [openAccountPopover, setOpenAccountPopover] = useState(false);
  const [openCreatePopover, setOpenCreatePopover] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPromptCreateMunicipality, setShowPromptCreateMunicipality] = useState(false);
  const [showPromptCreateForm, setShowPromptCreateForm] = useState(false);
  const [showPromptExport, setShowPromptExport] = useState(false);

  const signout = async () => {
    try {
      setLoading(true);
      await UserService.logout();
      setLoading(false);
      navigate('/login');
    } catch (e: any) {
      ErrorService.handleError(e, dispatch);
      setLoading(false);
    }
  };

  return (
    <>
      <NavbarRoot
        sx={{
          left: {
            lg: 280,
          },
          width: {
            lg: 'calc(100% - 280px)',
          },
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: 64,
            left: 0,
            px: 2,
          }}
        >
          <IconButton
            onClick={onSidebarOpen}
            sx={{
              display: {
                xs: 'inline-flex',
                lg: 'none',
              },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          <Box>
            {showBack && <Link
              color="secondary"
              onClick={() => navigate(-1)}
              sx={{
                textDecoration: 'none',
                fontSize: '12px',
                cursor: 'pointer',
              }}><ArrowBack sx={{ fontSize: '10px' }} />retour</Link>}
            <Typography variant="h5" color="black">{title}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          {
            user?.permission === (Permissions.ADMIN || Permissions.SUPER_ADMIN)
              && <Box sx={{ display: 'flex', gap: '15px' }}>
                <Tooltip title="Exporter les formulaires">
                  <Button color="error" variant="outlined" onClick={() => setShowPromptExport(true)}>
                  Exporter
                  </Button>
                </Tooltip>
                <Button
                  onClick={() => setOpenCreatePopover(true)}
                  ref={createRef}
                  variant="contained"
                  startIcon={<AddIcon/>}
                >
                  {Utils.getTranslation(translator.components.appBar.add)}
                </Button>
              </Box>
          }
          <IconButton
            onClick={() => setOpenAccountPopover(true)}
            ref={settingsRef}
            sx={{ ml: 1, mr: '10px' }}
          >
            <MoreVertIcon fontSize='medium'/>
          </IconButton>
        </Toolbar>
      </NavbarRoot>
      <Popover
        anchorEl={createRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        onClose={() => setOpenCreatePopover(false)}
        open={openCreatePopover}
        PaperProps={{
          sx: { width: '300px' },
        }}
      >
        <MenuList>
          <MenuItem onClick={() => setShowPromptCreateMunicipality(true)}>
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            {Utils.getTranslation(translator.components.appBar.createPopover.municipality)}
          </MenuItem>
          <MenuItem onClick={() => setShowPromptCreateForm(true)}>
            <ListItemIcon>
              <VerticalSplitIcon />
            </ListItemIcon>
            {Utils.getTranslation(translator.components.appBar.createPopover.form)}
          </MenuItem>
        </MenuList>
      </Popover>
      <Popover
        anchorEl={settingsRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        onClose={() => setOpenAccountPopover(false)}
        open={openAccountPopover}
        PaperProps={{
          sx: { width: '300px' },
        }}
      >
        <Box
          sx={{
            py: 1.5,
            px: 2,
          }}
        >
          <Typography variant="overline">
            {
              user?.permission === Permissions.MUNICIPALITY
                ? Utils.getTranslation(translator.components.appBar.accountPopover.title.municipality)
                : Utils.getTranslation(translator.components.appBar.accountPopover.title.admin)
            }

          </Typography>
          <Typography
            color="text.secondary"
            variant="body2"
          >
            {
              user?.permission === Permissions.MUNICIPALITY
                ? user?.name
                : user?.email
            }
          </Typography>
        </Box>
        <MenuList
          sx={{
            '& > *': {
              '&:first-of-type': {
                borderTopColor: 'divider',
                borderTopStyle: 'solid',
                borderTopWidth: '1px',
              },
              padding: '12px 16px',
            },
          }}
        >
          <MenuItem onClick={() => navigate('/settings')}>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            {Utils.getTranslation(translator.components.appBar.accountPopover.settings)}
          </MenuItem>
          <MenuItem onClick={() => signout()}>
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            {Utils.getTranslation(translator.components.appBar.accountPopover.signOut)}
          </MenuItem>
        </MenuList>
      </Popover>
      <PromptCreateMunicipality setShow={setShowPromptCreateMunicipality} show={showPromptCreateMunicipality} />
      <PromptCreateForm setShow={setShowPromptCreateForm} show={showPromptCreateForm} />
      <PromptExport setShow={setShowPromptExport} show={showPromptExport} />
      <Spinner show={loading}/>
    </>
  );
};

export default Navbar;
